import React, {
  useState,
  useEffect,
  memo,
  CSSProperties,
  useMemo,
} from "react";
import { Accept, DropzoneOptions, useDropzone } from "react-dropzone";
import {
  InsertDriveFileRounded,
  MoreVertOutlined,
  Upload,
} from "@mui/icons-material";
import { Box, IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import styled from "styled-components";
import { formatBytes, formatDate, isImage } from "../utils";
import { megabytes } from "../constant";
import Typography from "component/MarketPlace/Typography/Typography";
import { grey } from '@mui/material/colors';
import parse from "html-react-parser";
import { AttachmentWrapper, StyledAttachment } from "./index.style";

type FileWithPreview = {
  preview: string;
  lastModifiedDate: string & Date;
} & File;

type AttachmentConfig = {
  acceptFiles?: Accept;
  maxSize?: number;
  maxFiles?: number;
  disabled?: boolean;
};

type Props = {
  onDelete: (filePreview: string) => void;
  files: FileWithPreview[];
  onSetFiles: (file: FileWithPreview[]) => void;
  hidden?: boolean;
  preview?: boolean;
  config?: AttachmentConfig;
  locale?: any;
  attachmentOptions?: DropzoneOptions;
};


type Styles = {
  style?: CSSProperties;
};

const ThumbContent = styled(Box)<Styles>(() => ({
  display: "inline-flex",
  marginBottom: 16,
  width: "100%",
  height: 164,
  boxSizing: "border-box",
}));

const ThumbInner = styled(Box)<Styles>(() => ({
  display: "flex",
  minWidth: "100%",
  overflow: "hidden",
  position: "relative",
}));

const ThumbIconContent = styled(Box)<Styles>(() => ({
  position: "absolute",
  zIndex: 100,
  right: 0,
  transform: "translate(-7px, 14px)",
}));

const Image = styled.img<Styles>(() => ({
  display: "block",
  width: "100%",
  height: "100%",
  objectFit: "cover",
  borderRadius: 8,
}));

const ThumbsContainer = styled(Box)<Styles>(() => ({
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  marginTop: 16,
}));


const UploadFileIcon = (disabled : any) => {
  return (
    <Box 
      sx={{
        backgroundColor:'action.active' ,
        borderRadius:'100px',
        padding:'6px',
        display:'flex',
        justifyContent:'center',
        alignItems:'center',
        "& .MuiSvgIcon-root": {
          color: 'primary.contrastText',
        }
      }}
    >
      <Upload fontSize="medium" />
    </Box>
  );
};

const StyledCard = styled(Box)<Styles>(({ theme }) => ({
  backgroundColor: grey[200],
  borderRadius: "8px",
  height: "100%",
  width: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
}));

const StyledFileName = styled(Typography)<Styles>(({ theme }) => ({
  position: "absolute",
  bottom: "0px",
  color: "inherit.white",
  transform: "translate(12px, -9px)",
}));

const StyledInsertDriveFile = styled(InsertDriveFileRounded)(() => ({
  fontSize: "3rem !important",
}));

const FileIcon = ({ fileName }: { fileName: string }) => {
  const fileType = fileName.split(".").pop()?.toUpperCase() || "";

  return (
    <Box 
      sx={{
        position: 'relative', 
        "& .MuiSvgIcon-root": {
          color: 'action.active',
        }
      }}
    >
      <StyledFileName variant="body2" color="inherit.white">{fileType}</StyledFileName>
      <StyledInsertDriveFile />
    </Box>
  );
};

const FilePreview = ({
  file,
  onDelete,
}: {
  file: FileWithPreview;
  onDelete: (filePreview: string) => void;
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDelete = (filePreview: string) => {
    onDelete(filePreview);
    handleClose();
  };

  const fileType = file?.name.split(".").pop();
  const fileSize = file?.size;

  return (
    <>
      <ThumbContent>
        <ThumbInner>
          <ThumbIconContent>
            <Tooltip title="Actions">
              <>
                <IconButton onClick={handleOpenMenu} aria-label="More Button">
                  <MoreVertOutlined fontSize="medium" htmlColor={grey[500]} />
                </IconButton>
                <Menu
                  key={file.preview}
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleClose}
                  disableScrollLock
                >
                  <MenuItem
                    key={file.preview}
                    onClick={() => handleDelete(file.preview)}
                  >
                    Delete
                  </MenuItem>
                </Menu>
              </>
            </Tooltip>
          </ThumbIconContent>
          {isImage(file) ? (
            <Image
              src={file.preview}
              // Revoke data uri after image is loaded
              onLoad={() => {
                URL.revokeObjectURL(file.preview);
              }}
            />
          ) : (
            <StyledCard>
              <FileIcon fileName={file.name} />
            </StyledCard>
          )}
        </ThumbInner>
      </ThumbContent>
      <Box sx={{display: "flex", alignItems: "center"}}>
        <FileIcon fileName={file.name} />
        <Box>
          <Typography variant="body2" color="text.primary">
            {file?.name?.length > 20
              ? `${file?.name.slice(0, 20)} ... ${fileType}`
              : file?.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatDate(file?.lastModifiedDate)} • {formatBytes(fileSize)}
          </Typography>
        </Box>
      </Box>
    </>
  );
};

const Attachments = ({
  files,
  config = {},
  hidden = false,
  preview = true,
  onSetFiles = () => {},
  onDelete,
  locale,
  attachmentOptions,
}: Props) => {
  const {
  acceptFiles = {
    'image/jpeg': [],
    'image/png': [],
    'text/plain': ['.txt'],
    'application/pdf': []
  },
    maxSize = 20,
    maxFiles = Infinity,
    disabled: configDisabled = false,
  } = config;

  const disabled = configDisabled || files.length >= maxFiles;

  const { getRootProps, getInputProps } = useDropzone({
    accept: acceptFiles,
    onDrop: (acceptedFiles: File[]) => {
      const newFile = acceptedFiles.map((file) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        })
      ) as FileWithPreview[];
      onSetFiles(newFile);
    },
    maxSize: maxSize * megabytes,
    maxFiles: maxFiles,
    disabled: disabled,
    ...attachmentOptions
  });

  const thumbs = useMemo(() => {
    return files.map((file) => {
      return (
        <Box
          sx={{
            width: {
              xs: "100%",
              sm: "auto",
            },
            margin: {
              xs: "16px",
              sm: "24px 24px 24px 0px",
            },
          }}
          key={file.preview}
        >
          <FilePreview file={file} onDelete={onDelete} />
        </Box>
      );
    });
  }, [files, onDelete]);

  useEffect(() => {
    // Make sure to revoke the data uris to avoid memory leaks, will run on unmount
    return () => files.forEach((file) => URL.revokeObjectURL(file.preview));
  }, [files]);

  return (
    <AttachmentWrapper disabled={disabled}>
      {!hidden && (
        <Box {...getRootProps({ className: "dropzone" })} id='upl-dropzone-attachment'>
          <input {...getInputProps()} />
          <Box         
            sx={{
              flexDirection: "column",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              border:'1.5px dashed',
              padding: "24px",
              borderColor:"action.active",
              gap:'8px',
              borderRadius: "8px",
            }}
          >
            {UploadFileIcon(disabled)}
            <Typography 
              variant='body1' 
              color='text.primary' 
            >
              <StyledAttachment disabled={disabled}>
                {parse(locale?.uploadAttachment?.dropZoneText_alt || '') as string}
              </StyledAttachment>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              JPG, PNG, PDF, TXT — {maxSize}mb {locale?.uploadAttachment?.configText.limit}
            </Typography>
          </Box>
        </Box>
      )}
      {preview && <ThumbsContainer>{thumbs}</ThumbsContainer>}
    </AttachmentWrapper>
  );
};

export default memo(Attachments);
